import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Heart, Users, MapPin, Target, TrendingUp, Shield, Award, ArrowRight, Utensils, Home, Baby, User, Gift, Globe } from "lucide-react";

function FeedHungrySouls() {
  const [isVisible, setIsVisible] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  const heroImages = [
    "img/user9.JPG",
    "img/use1.JPG",
    "img/use5.JPG"
  ];

  const galleryImages = [
    "img/use1.JPG",
    "img/use5.JPG",
    "img/feed2.jpeg",
    "img/feedmain1.jpeg",
    "img/feed4.jpeg"
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => {
      observer.disconnect();
      clearInterval(imageInterval);
    };
  }, []);

  const impactStats = [
    {
      icon: Users,
      number: "2,500+",
      label: "Lives Touched",
      description: "Families supported across Kayunga",
      color: "from-rose-500 to-pink-500"
    },
    {
      icon: Utensils,
      number: "15,000+",
      label: "Meals Provided",
      description: "Nutritious meals distributed monthly",
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: MapPin,
      number: "25+",
      label: "Villages Reached",
      description: "Rural communities in Kayunga district",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Heart,
      number: "100%",
      label: "Direct Impact",
      description: "Every donation reaches those in need",
      color: "from-purple-500 to-violet-500"
    }
  ];

  const beneficiaries = [
    {
      icon: User,
      title: "Elderly Community",
      description: "Providing dignified support to our respected elders who have contributed to society",
      impact: "Nutritional Security",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Baby,
      title: "Single Mothers",
      description: "Empowering single mothers with food security to focus on family stability",
      impact: "Family Empowerment",
      color: "from-rose-500 to-pink-500"
    },
    {
      icon: Users,
      title: "Unemployed Families",
      description: "Bridge support during employment transitions and economic hardship",
      impact: "Economic Relief",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Shield,
      title: "Special Needs Groups",
      description: "Specialized care and nutrition for individuals with unique requirements",
      impact: "Inclusive Care",
      color: "from-purple-500 to-violet-500"
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Mission Overview', icon: Target },
    { id: 'impact', label: 'Our Impact', icon: TrendingUp },
    { id: 'beneficiaries', label: 'Who We Serve', icon: Users },
    { id: 'gallery', label: 'In Action', icon: Heart }
  ];



  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50">
      <Header />

      {/* Hero Section */}
      <div className="relative overflow-hidden pt-20 sm:pt-32 pb-12 sm:pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-rose-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-orange-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-amber-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>

          {/* Heart pattern */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 opacity-5">
            <Heart className="w-full h-full text-rose-600" fill="currentColor" />
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <div
              id="hero-content"
              data-animate
              className={`transform transition-all duration-1000 mt-20 sm:mt-0 text-center sm:text-left px-4 sm:px-0 ${isVisible['hero-content'] ? 'translate-x-0 opacity-100' : 'lg:-translate-x-full opacity-0'
                }`}
            >
              <div className="inline-flex items-center bg-rose-100 text-rose-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Heart className="w-4 h-4 mr-2" />
                Food Relief Initiative
              </div>

              <h1 className="text-4xl md:text-6xl font-black mb-6">
                <span className="bg-gradient-to-r from-rose-600 via-orange-600 to-amber-600 bg-clip-text text-transparent">
                  Feed The
                </span>
                <br />
                <span className="text-gray-800">Hungry Souls</span>
              </h1>

              <div className="bg-gradient-to-r from-rose-50 to-orange-50 border-l-4 border-rose-500 p-6 rounded-r-2xl mb-8">
                <p className="text-lg text-gray-700 font-medium">
                  <span className="font-bold text-rose-800">Nourishing Hope</span> in every meal.
                  We believe that no one should go hungry, and every person deserves dignity,
                  care, and the basic human right to nutritious food.
                </p>
              </div>

              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Our Food Relief project reaches out to elderly community members, single mothers,
                unemployed families, and special needs groups across Kayunga villages and neighborhoods,
                providing not just meals, but hope and human connection.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center sm:justify-start">
                <button
                  onClick={() => window.location.href = '/fund-our-work'}
                  className="bg-gradient-to-r from-rose-600 to-orange-600 hover:from-rose-700 hover:to-orange-700 text-white px-8 py-4 rounded-2xl font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center gap-2">
                  Make a Donation <Heart className="w-5 h-5" />
                </button>
                <button
                  onClick={() => window.location.href = '/contact-us'}
                  className="bg-white text-rose-600 border-2 border-rose-600 px-8 py-4 rounded-2xl font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:bg-rose-50 flex items-center justify-center gap-2">
                  Contact Us <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Image Carousel */}

            <div
              id="hero-image"
              data-animate
              className={`transform transition-all duration-1000 delay-300 ${isVisible['hero-image'] ? 'translate-x-0 opacity-100' : 'lg:translate-x-full opacity-0'
                }`}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <div className="relative h-96 md:h-[500px]">
                  <img
                    src={heroImages[currentImageIndex]}
                    alt="Feed The Hungry Souls"
                    className="w-full h-full object-cover transition-opacity duration-1000"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

                  {/* Overlay badge */}
                  <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-2">
                    <div className="flex items-center gap-2">
                      <Utensils className="w-5 h-5 text-rose-600" />
                      <span className="font-semibold text-gray-800">Food Relief Program</span>
                    </div>
                  </div>
                </div>

                {/* Image indicators */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50'
                        }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Impact Statistics Section */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div
            id="stats-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${isVisible['stats-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Transforming Lives Through <span className="bg-gradient-to-r from-rose-600 to-orange-600 bg-clip-text text-transparent">Compassion</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every meal we provide is more than nutrition—it's dignity, hope, and a reminder that someone cares
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-rose-500 to-orange-500 rounded-full mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  id={`stat-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${isVisible[`stat-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-rose-500">
                    <div className={`w-16 h-16 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-black text-gray-800 mb-2">{stat.number}</div>
                      <div className="text-lg font-semibold text-rose-600 mb-2">{stat.label}</div>
                      <div className="text-sm text-gray-600">{stat.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Interactive Tabs Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center mb-12">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold text-lg transition-all duration-300 mx-2 mb-2 ${activeTab === tab.id
                    ? 'bg-gradient-to-r from-rose-600 to-orange-600 text-white shadow-lg transform scale-105'
                    : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="transition-all duration-500">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-6">
                    A Mission of <span className="bg-gradient-to-r from-rose-600 to-orange-600 bg-clip-text text-transparent">Compassion</span>
                  </h3>
                  <div className="space-y-6 text-lg text-gray-700">
                    <p>
                      <strong>Feed The Hungry Souls</strong> represents more than a food relief program—
                      it embodies our commitment to human dignity and community solidarity. We understand
                      that hunger is not just about empty stomachs, but about broken spirits and lost hope.
                    </p>

                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 p-6 rounded-r-2xl">
                      <p className="font-semibold text-amber-800">
                        Our approach goes beyond immediate relief. We build relationships, restore dignity,
                        and create sustainable pathways out of food insecurity for the most vulnerable
                        members of our Kayunga communities.
                      </p>
                    </div>

                    <p>
                      Every meal distributed carries with it a message of hope: "You matter. You are not forgotten.
                      Your community cares about you." This philosophy drives everything we do, from sourcing
                      nutritious local ingredients to training our volunteers in compassionate service.
                    </p>
                  </div>
                </div>
                <div className="relative">
                  <img
                    src="img/user9.JPG"
                    alt="Community outreach"
                    className="w-full h-80 object-cover rounded-3xl shadow-2xl"
                  />
                </div>
              </div>
            )}

            {activeTab === 'impact' && (
              <div className="text-center">
                <h3 className="text-3xl font-bold text-gray-800 mb-8">
                  Measurable <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Change</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <div className="bg-gradient-to-br from-rose-600 to-pink-600 rounded-3xl p-8 text-white">
                    <div className="text-4xl font-black mb-4">Zero</div>
                    <div className="text-lg font-semibold mb-2">Overhead Waste</div>
                    <div className="text-rose-100">Every donation goes directly to food and logistics</div>
                  </div>
                  <div className="bg-gradient-to-br from-orange-600 to-amber-600 rounded-3xl p-8 text-white">
                    <div className="text-4xl font-black mb-4">365</div>
                    <div className="text-lg font-semibold mb-2">Days Active</div>
                    <div className="text-orange-100">Year-round commitment to our community</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-3xl p-8 text-white">
                    <div className="text-4xl font-black mb-4">Local</div>
                    <div className="text-lg font-semibold mb-2">Sourcing Priority</div>
                    <div className="text-green-100">Supporting local farmers and economy</div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-3xl p-8">
                  <h4 className="text-2xl font-bold text-gray-800 mb-4">Transparency Promise</h4>
                  <p className="text-lg text-gray-600">
                    We provide quarterly impact reports to all donors, showing exactly how your
                    contributions transform lives. Every dollar is tracked, every meal is documented,
                    and every story of hope is shared with those who make it possible.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'beneficiaries' && (
              <div>
                <h3 className="text-3xl font-bold text-gray-800 mb-12 text-center">
                  Those We <span className="bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">Serve</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {beneficiaries.map((group, index) => {
                    const Icon = group.icon;
                    return (
                      <div key={index} className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                        <div className={`w-16 h-16 bg-gradient-to-r ${group.color} rounded-2xl flex items-center justify-center mb-6`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-800 mb-4">{group.title}</h4>
                        <p className="text-gray-600 mb-4">{group.description}</p>
                        <div className="inline-flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                          <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                          {group.impact}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h3 className="text-3xl font-bold text-gray-800 mb-12 text-center">
                  Stories of <span className="bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Hope</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {galleryImages.filter(img => img).map((image, index) => (
                    <div key={index} className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                      <img
                        src={image}
                        alt={`Feed The Hungry Souls activity ${index + 1}`}
                        className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-4 left-4 text-white">
                          <div className="font-semibold">Community Outreach</div>
                          <div className="text-sm text-gray-200">Feed The Hungry Souls</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-rose-600 via-orange-600 to-amber-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
              }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <Gift className="w-5 h-5 mr-3" />
              Your Impact • Their Hope • Our Mission
            </div>

            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Every Meal Matters
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Join us in creating a world where no one goes hungry. Your generosity transforms
              not just empty plates, but empty hearts. Together, we can nourish hope, one soul at a time.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                onClick={() => window.location.href = '/fund-our-work'}
                className="bg-white text-rose-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
                <Heart className="w-5 h-5" />
                Donate Now
              </button>
              <button
                onClick={() => window.location.href = '/contact-us'}
                className="bg-white/20 backdrop-blur-sm text-white border-2 border-white/30 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 hover:bg-white/30 flex items-center justify-center gap-2">
                <Users className="w-5 h-5" />
                Contact Us
              </button>
            </div>

            <div className="mt-12 text-white/80">
              <p className="text-lg">$25 feeds a family for a week • $100 supports 4 families • $500 transforms a village</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default FeedHungrySouls;